import {Component, inject, Input, OnDestroy} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {MatDialog} from "@angular/material/dialog";
import {selectIsAnyAssetFormInEditMode} from "../../store/asset/asset.selectors";
import {AssetValuationActions, AssetValuationDetailActions} from "../../store/asset.actions";
import {FundActions} from "../../../fund/store/fund.actions";
import {AssetValuation} from "../../models/asset-valuation/asset-valuation";
import {distinctUntilChanged, filter, map, Subscription, take, withLatestFrom} from "rxjs";
import {AssetValuationTypeEnum} from "../../models/asset-valuation/asset-valuation-type.enum";
import {CodeTableEnum} from "../../../shared/model/code";
import {
    selectActiveAssetValuation,
    selectAssetValuationsForAssetAndFundValuation,
    selectFinancialOperationalCreateError,
    selectIsAssetValuationCreating,
    selectPreferredAssetValuationForAssetAndFundValuation
} from "../../store/asset-valuation/asset-valuation.selectors";
import {FundValuationManageDialogComponent} from "../../../fund/fund-details-page/fund-valuation-manage-dialog/fund-valuation-manage-dialog.component";
import {selectSelectedFundValuationsForFundReport} from "../../../fund/store/fund-valuation/fund-valuation.selectors";


@Component({
    selector: "valumize-asset-valuation-forecast",
    templateUrl: "./asset-valuation-forecast.component.html",
    styleUrls: ["./asset-valuation-forecast.component.scss"],
    standalone: false
})
export class AssetValuationForecastComponent implements OnDestroy {
    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    @Input() assetId?: number;
    @Input() fundId?: number;
    @Input() fundReportId?: number;
    @Input() fundValuationId?: number;

    subscriptions: Subscription[] = [];

    selectedAssetValuationsForAssetAndFundValuation$ = this.store.select(selectAssetValuationsForAssetAndFundValuation);
    selectedPreferredValuation$ = this.store.select(selectPreferredAssetValuationForAssetAndFundValuation);
    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    isCreatingValuation$ = this.store.select(selectIsAssetValuationCreating);
    fovCreateError = this.store.select(selectFinancialOperationalCreateError);
    selectedIndex$ = this.store.select(selectActiveAssetValuation).pipe(
        filter(av => av.status === "LOADED" || av.status === "SAVED"),
        withLatestFrom(this.selectedAssetValuationsForAssetAndFundValuation$),
        map(([av, valuations]) => {
            const tabTypes: string[] = [];
            if (valuations.some(v => v.valuationType.code === AssetValuationTypeEnum.DiscountMultipleValuation)) {
                tabTypes.push(AssetValuationTypeEnum.DiscountMultipleValuation);
            }
            if (valuations.some(v => v.valuationType.code === AssetValuationTypeEnum.FinancialOperationalValuation)) {
                tabTypes.push(AssetValuationTypeEnum.FinancialOperationalValuation);
            }
            return tabTypes.indexOf(av.data.valuationType.code ?? "");
        }),
        filter(index => index >= 0),
        distinctUntilChanged()
    );

    protected readonly valuationTypeEnum = AssetValuationTypeEnum;
    codeTableValuationType = CodeTableEnum.ASSET_VALUATIONTYPE;

    constructor() {
    }

    onTabChange(index: number, valuations: AssetValuation[]) {
        const tabTypes: string[] = [];
        if (this.valuationTypeExists(valuations, AssetValuationTypeEnum.DiscountMultipleValuation)) {
            tabTypes.push(AssetValuationTypeEnum.DiscountMultipleValuation);
        }
        if (this.valuationTypeExists(valuations, AssetValuationTypeEnum.FinancialOperationalValuation)) {
            tabTypes.push(AssetValuationTypeEnum.FinancialOperationalValuation);
        }

        const selectedType = tabTypes[index];
        if (!selectedType || !this.assetId) return;

        const valuation = valuations.find(v => v.valuationType.code === selectedType);
        if (valuation?.id) {
            this.store.dispatch(AssetValuationDetailActions.loadDetail({
                assetId: this.assetId,
                assetValuationId: valuation.id
            }));
        }
    }

    valuationTypeExists(valuations: AssetValuation[], valuationType: string) {
        return valuations.some(v => v.valuationType.code === valuationType);
    }

    filterExisting(valuations: AssetValuation[]) {
        return Object.values(AssetValuationTypeEnum).filter(type => !this.valuationTypeExists(valuations, type));
    }

    createAssetValuation(valuationType: string) {
        if (!this.assetId || !this.fundValuationId) {
            return;
        }
        this.store.dispatch(AssetValuationDetailActions.create({
            assetId: this.assetId,
            fundValuationId: this.fundValuationId,
            valuationType
        }));
    }

    openSelectValuationDialog(fundId: number, fundReportId: number): void {
        this.store.select(selectSelectedFundValuationsForFundReport).pipe(take(1)).subscribe(valuations => {
            if (valuations.data.length === 0) {
                this.store.dispatch(FundActions.loadFundData({fundId, fundReportId}));
            }
        });

        const dialogRef = this.dialog.open(FundValuationManageDialogComponent, {width: "850px", height: "650px"});
        dialogRef.componentInstance.fundId = fundId;
        dialogRef.componentInstance.fundReportId = fundReportId;
        dialogRef.componentInstance.currentFundValuationId = this.fundValuationId;
        dialogRef.componentInstance.returnToAsset = true;

        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((selectedFundValuationId: number | undefined) => {
                if (selectedFundValuationId != null) {
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: {fundValuationId: selectedFundValuationId},
                        queryParamsHandling: "merge"
                    });
                }
            })
        );
    }

    changePreferredType(valuationId: number | string) {
        if (!this.assetId || !valuationId) {
            return;
        }

        //cast to a number (mat-select emits the option value, which is the valuation id)
        const assetValuationId = +valuationId;

        if (isNaN(assetValuationId)) {
            this.store.dispatch(AssetValuationActions.changePreferedValuationError({errorMsg: "Could not find valuation to set as preferred. Please refresh."}));
        } else {
            this.store.dispatch(AssetValuationActions.changePreferedValuation({assetId: this.assetId, assetValuationId}))
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
